/**
 * Edges and arcs (1.K-2 deliverable 4). Each placement has a start edge (node 2p) and an end edge
 * (node 2p + 1), p its index in the sorted placement list. An edge with a time point is fixed: it
 * reports that time point's interval and carries a working interval through propagation. The arcs
 * of the distance graph say how bounds pass from edge to edge.
 */
import { TICK_MAX, TICK_MIN } from '@codex/contracts';
import { exactSeconds, isCalendarUnit } from './lags.js';
import { float64, int32, uint8 } from './pool.js';
export const startOf = (p) => 2 * p;
export const endOf = (p) => 2 * p + 1;
export const placementOf = (edge) => edge >> 1;
/** The tick range's width: what an exact minimum that overflows saturates to. */
export const RANGE_WIDTH = TICK_MAX - TICK_MIN;
export function edgesOf(p) {
    if (p.kind === 'undated')
        return { start: null, end: null, inverted: false };
    if (p.kind === 'moment')
        return { start: p.start, end: p.start, inverted: false };
    const { start, end } = p;
    const inverted = start !== null && end !== null && start.lo !== null && end.hi !== null && end.hi <= start.lo;
    return { start, end, inverted };
}
/** The node of a constraint's stored edge on one of its placements. */
export function edgeNode(placement, edge) {
    return edge === 'start' ? startOf(placement) : endOf(placement);
}
/** The order-graph arcs of a constraint (a `same_time` gives its one link). */
export function orderArcs(c) {
    if (c.relation === 'during') {
        return [
            [startOf(c.before), startOf(c.after)],
            [endOf(c.after), endOf(c.before)],
        ];
    }
    return [[edgeNode(c.before, c.beforeEdge), edgeNode(c.after, c.afterEdge)]];
}
/** Arc kinds: an exact weight, a calendar-unit minimum (with the order), a calendar-unit maximum. */
export const EXACT = 0;
export const MIN = 1;
export const MAX = 2;
export class ArcList {
    tail;
    head;
    kind;
    w;
    lag = [];
    c;
    reverse;
    pinned;
    length = 0;
    /** Room for `capacity` arcs: a placement has one, a constraint at most two. */
    constructor(capacity) {
        this.tail = int32(capacity);
        this.head = int32(capacity);
        this.kind = uint8(capacity);
        this.w = float64(capacity);
        this.c = int32(capacity);
        this.reverse = uint8(capacity);
        this.pinned = uint8(capacity);
    }
    push(tail, head, kind, w, lag, c, reverse, pinned) {
        const i = this.length++;
        this.tail[i] = tail;
        this.head[i] = head;
        this.kind[i] = kind;
        this.w[i] = w;
        this.lag.push(lag);
        this.c[i] = c;
        this.reverse[i] = reverse ? 1 : 0;
        this.pinned[i] = pinned ? 1 : 0;
    }
}
/** Whether a constraint carries a lag at all (a minimum above 0 or any maximum). */
export function hasLag(c) {
    return (c.relation === 'before' && c.lagUnit !== null && ((c.minLagN ?? 0) > 0 || c.maxLagN !== null));
}
/** Whether its lag is in months or years. */
export function hasCalendarLag(c) {
    return hasLag(c) && isCalendarUnit(c.lagUnit);
}
/**
 * The distance-graph arcs of a kept constraint, added to `out` (the forward arc first). Without a
 * compiled calendar every lag is left out and the pair stays ordered; `lagFor` gives the month or
 * year arithmetic of a unit.
 */
export function constraintArcs(c, calendar, lagFor, pinned, out) {
    if (c.relation === 'during') {
        for (const [t, h] of orderArcs(c))
            out.push(t, h, EXACT, 0, null, c.age, false, pinned);
        return;
    }
    const x = edgeNode(c.before, c.beforeEdge);
    const y = edgeNode(c.after, c.afterEdge);
    if (c.relation === 'same_time') {
        out.push(x, y, EXACT, 0, null, c.age, false, pinned);
        out.push(y, x, EXACT, 0, null, c.age, false, pinned);
        return;
    }
    if (calendar === null || !hasLag(c)) {
        out.push(x, y, EXACT, 0, null, c.age, false, pinned);
        return;
    }
    const unit = c.lagUnit;
    const min = c.minLagN ?? 0;
    const max = c.maxLagN;
    if (isCalendarUnit(unit)) {
        const lag = lagFor(unit);
        if (min > 0)
            out.push(x, y, MIN, min, lag, c.age, false, pinned);
        else
            out.push(x, y, EXACT, 0, null, c.age, false, pinned);
        if (max !== null)
            out.push(y, x, MAX, max, lag, c.age, true, pinned);
        return;
    }
    const w = min > 0 ? (exactSeconds(calendar, min, unit) ?? RANGE_WIDTH) : 0;
    out.push(x, y, EXACT, w, null, c.age, false, pinned);
    const seconds = max === null ? null : exactSeconds(calendar, max, unit);
    if (seconds !== null)
        out.push(y, x, EXACT, -seconds, null, c.age, true, pinned);
}
/** A placement's start-to-end arc (none for an inverted one), added to `out`. */
export function placementArc(p, pinned, out) {
    out.push(startOf(p), endOf(p), EXACT, 0, null, -1, false, pinned);
}
/** Copies a pass's arcs out of the scratch shelves. */
export function snapshotArcs(arcs, unitOf) {
    const m = arcs.length;
    const units = [];
    for (let i = 0; i < m; i++) {
        const lag = arcs.lag[i] ?? null;
        units.push(lag === null ? null : unitOf(lag));
    }
    return {
        tail: arcs.tail.slice(0, m),
        head: arcs.head.slice(0, m),
        kind: arcs.kind.slice(0, m),
        w: arcs.w.slice(0, m),
        units,
        c: arcs.c.slice(0, m),
        reverse: arcs.reverse.slice(0, m),
        pinned: arcs.pinned.slice(0, m),
        length: m,
    };
}
/** A snapshot's arcs for this solve: the same arrays, with this solve's month and year arithmetic. */
export function restoreArcs(s, lagOf) {
    const lag = [];
    for (let i = 0; i < s.length; i++) {
        const unit = s.units[i] ?? null;
        lag.push(unit === null ? null : lagOf(unit));
    }
    return {
        tail: s.tail,
        head: s.head,
        kind: s.kind,
        w: s.w,
        lag,
        c: s.c,
        reverse: s.reverse,
        pinned: s.pinned,
        length: s.length,
    };
}
//# sourceMappingURL=graph.js.map